import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { InvoiceAPI, UsersAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatCurrency, formatDate, STATUS_STYLES, STATUS_OPTIONS } from "../lib/format";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, MagnifyingGlass, Trash, Eye, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function InvoicesList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [invoices, setInvoices] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    const ownerId = isAdmin && ownerFilter !== "all" ? ownerFilter : undefined;
    InvoiceAPI.list(ownerId)
      .then(setInvoices)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [ownerFilter, isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      UsersAPI.list()
        .then((list) => setSubs(list.filter((u) => u.role === "subcontractor")))
        .catch(() => {});
    }
  }, [isAdmin]);

  const filtered = invoices.filter((inv) => {
    const q = search.toLowerCase();
    const matchesQ =
      !q ||
      inv.invoice_number.toLowerCase().includes(q) ||
      (inv.client_name || "").toLowerCase().includes(q);
    const matchesStatus = statusFilter === "all" || inv.status === statusFilter;
    return matchesQ && matchesStatus;
  });

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await InvoiceAPI.remove(deleteId);
      toast.success("Invoice deleted");
      setDeleteId(null);
      load();
    } catch {
      toast.error("Failed to delete invoice");
    }
  };

  return (
    <div data-testid="invoices-list-page">
      <PageHeader
        overline="Records"
        title="Invoices"
        description="All issued invoices, sorted by most recent."
        actions={
          <Link to="/invoices/new">
            <Button data-testid="new-invoice-btn" className="rounded-sm">
              <Plus size={16} weight="bold" className="mr-2" />
              New Invoice
            </Button>
          </Link>
        }
      />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            placeholder="Search by invoice # or client…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-sm"
            data-testid="invoice-search-input"
          />
        </div>
        {isAdmin && (
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger
              className="w-full sm:w-56 rounded-sm"
              data-testid="invoice-owner-filter"
            >
              <SelectValue placeholder="All subcontractors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All users</SelectItem>
              <SelectItem value={user.id}>Me ({user.name || "Admin"})</SelectItem>
              {subs.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name} ({s.email})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger
            className="w-full sm:w-48 rounded-sm"
            data-testid="invoice-status-filter"
          >
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="border border-border bg-white">
        <div className="hidden lg:grid grid-cols-12 gap-4 px-6 py-3 border-b border-border bg-muted/40 overline text-muted-foreground">
          <div className="col-span-2">Invoice #</div>
          <div className="col-span-3">Client</div>
          <div className="col-span-2">Issued</div>
          <div className="col-span-2">Due</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1 text-right">Total</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-sm text-muted-foreground mb-4">
              {invoices.length === 0
                ? "No invoices yet."
                : "No invoices match your filters."}
            </div>
            {invoices.length === 0 && (
              <Link to="/invoices/new">
                <Button data-testid="empty-new-invoice-btn" className="rounded-sm">
                  <Plus size={16} weight="bold" className="mr-2" />
                  Create your first invoice
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filtered.map((inv) => (
              <div
                key={inv.id}
                className="px-4 py-3 sm:px-6 sm:py-4 hover:bg-muted/40 transition-colors"
                data-testid={`invoice-row-${inv.invoice_number}`}
              >
                {/* Mobile card layout */}
                <div className="lg:hidden flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-mono text-sm font-semibold">
                        {inv.invoice_number}
                      </div>
                      <div className="text-sm truncate mt-0.5">{inv.client_name}</div>
                      {isAdmin && inv.owner_name && (
                        <div className="text-[11px] text-muted-foreground truncate">
                          by {inv.owner_name}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono text-sm font-bold">
                        {formatCurrency(inv.total, inv.currency)}
                      </div>
                      <span
                        className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 border mt-1 ${
                          STATUS_STYLES[inv.status] || ""
                        }`}
                      >
                        {inv.status}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                    <div>
                      <span className="overline">Due</span> · {formatDate(inv.due_date)}
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => navigate(`/invoices/${inv.id}`)}
                        className="p-1.5 hover:bg-muted rounded-sm"
                        data-testid={`view-invoice-${inv.invoice_number}`}
                        aria-label="View"
                      >
                        <Eye size={16} weight="bold" />
                      </button>
                      <button
                        onClick={() => navigate(`/invoices/${inv.id}/edit`)}
                        className="p-1.5 hover:bg-muted rounded-sm"
                        data-testid={`edit-invoice-${inv.invoice_number}`}
                        aria-label="Edit"
                      >
                        <PencilSimple size={16} weight="bold" />
                      </button>
                      <button
                        onClick={() => setDeleteId(inv.id)}
                        className="p-1.5 hover:bg-destructive/10 hover:text-destructive rounded-sm"
                        data-testid={`delete-invoice-${inv.invoice_number}`}
                        aria-label="Delete"
                      >
                        <Trash size={16} weight="bold" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Desktop grid layout */}
                <div className="hidden lg:grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-2 font-mono text-sm font-semibold">
                    {inv.invoice_number}
                  </div>
                  <div className="col-span-3 text-sm truncate">
                    <div className="truncate">{inv.client_name}</div>
                    {isAdmin && inv.owner_name && (
                      <div className="text-[11px] text-muted-foreground truncate">
                        by {inv.owner_name}
                      </div>
                    )}
                  </div>
                  <div className="col-span-2 text-xs text-muted-foreground">
                    {formatDate(inv.issue_date)}
                  </div>
                  <div className="col-span-2 text-xs text-muted-foreground">
                    {formatDate(inv.due_date)}
                  </div>
                  <div className="col-span-1">
                    <span
                      className={`inline-block text-xs font-semibold px-2 py-0.5 border ${
                        STATUS_STYLES[inv.status] || ""
                      }`}
                    >
                      {inv.status}
                    </span>
                  </div>
                  <div className="col-span-1 text-right font-mono text-sm font-semibold">
                    {formatCurrency(inv.total, inv.currency)}
                  </div>
                  <div className="col-span-1 flex justify-end gap-1">
                    <button
                      onClick={() => navigate(`/invoices/${inv.id}`)}
                      className="p-1.5 hover:bg-muted rounded-sm"
                      aria-label="View"
                    >
                      <Eye size={16} weight="bold" />
                    </button>
                    <button
                      onClick={() => navigate(`/invoices/${inv.id}/edit`)}
                      className="p-1.5 hover:bg-muted rounded-sm"
                      aria-label="Edit"
                    >
                      <PencilSimple size={16} weight="bold" />
                    </button>
                    <button
                      onClick={() => setDeleteId(inv.id)}
                      className="p-1.5 hover:bg-destructive/10 hover:text-destructive rounded-sm"
                      aria-label="Delete"
                    >
                      <Trash size={16} weight="bold" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the invoice. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="confirm-delete-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
